/**
 * React Hook for Local AI Management
 * Handles initialization, provider switching, and AI generation
 */

import { useState, useEffect, useCallback } from 'react';
import { localAI, AIMode, AIProvider } from '../services/localAI';

export interface UseLocalAIReturn {
  mode: AIMode;
  provider: AIProvider | null;
  isInitializing: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  generate: (prompt: string, chatHistory?: Array<{ role: 'user' | 'assistant'; content: string }>, chatId?: string) => AsyncGenerator<any>;
  switchProvider: (providerName: string) => Promise<boolean>;
  hasOllama: boolean;
  hasWebLLM: boolean;
  setupOllama: () => void;
  initializeWebLLM: () => Promise<void>;
  isWebLLMLoading: boolean;
}

// ============================================================
// MODULE-LEVEL GUARDS - These persist across component remounts!
// This is CRITICAL to prevent repeated API calls
// ============================================================
let GLOBAL_HAS_INITIALIZED = false;
let GLOBAL_INIT_IN_PROGRESS = false;
let GLOBAL_CACHED_MODE: AIMode = 'detecting';
let GLOBAL_CACHED_PROVIDER: AIProvider | null = null;

export const useLocalAI = (): UseLocalAIReturn => {
  // Use cached values as initial state to prevent re-initialization
  const [mode, setMode] = useState<AIMode>(GLOBAL_CACHED_MODE);
  const [provider, setProvider] = useState<AIProvider | null>(GLOBAL_CACHED_PROVIDER);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isWebLLMLoading, setIsWebLLMLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialize = useCallback(async () => {
    // CRITICAL: Use module-level guards that persist across remounts
    if (GLOBAL_INIT_IN_PROGRESS) {
      return;
    }
    if (GLOBAL_HAS_INITIALIZED) {
      // Just sync state with cached values, don't make API call
      setMode(GLOBAL_CACHED_MODE);
      setProvider(GLOBAL_CACHED_PROVIDER);
      return;
    }

    GLOBAL_INIT_IN_PROGRESS = true;
    setIsInitializing(true);
    setError(null);

    try {
      const detectedMode = await localAI.initialize();
      const detectedProvider = localAI.getCurrentProvider();

      // Update module-level cache
      GLOBAL_CACHED_MODE = detectedMode;
      GLOBAL_CACHED_PROVIDER = detectedProvider;
      GLOBAL_HAS_INITIALIZED = true;

      // Update React state
      setMode(detectedMode);
      setProvider(detectedProvider);

      if (detectedMode === 'unavailable') {
        setError('No AI providers available. Please install Ollama or check your connection.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize AI');
      setMode('unavailable');
      GLOBAL_CACHED_MODE = 'unavailable';
    } finally {
      setIsInitializing(false);
      GLOBAL_INIT_IN_PROGRESS = false;
    }
  }, []);

  const generate = useCallback(async function* (prompt: string, chatHistory?: Array<{ role: 'user' | 'assistant'; content: string }>, chatId?: string): AsyncGenerator<any> {
    // Check if we have a valid mode - use localAI directly instead of relying on React state
    const currentMode = localAI.getCurrentMode();
    const currentProvider = localAI.getCurrentProvider();

    if (currentMode !== 'backend' || !currentProvider) {
      throw new Error('No AI provider available');
    }

    try {
      yield* localAI.generate(prompt, chatHistory, chatId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
      throw err;
    }
  }, []);

  const switchProvider = useCallback(async (providerName: string): Promise<boolean> => {
    try {
      const success = await localAI.switchProvider(providerName);
      if (success) {
        const newMode = localAI.getCurrentMode();
        const newProvider = localAI.getCurrentProvider();
        setMode(newMode);
        setProvider(newProvider);
        GLOBAL_CACHED_MODE = newMode;
        GLOBAL_CACHED_PROVIDER = newProvider;
        setError(null);
      }
      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch provider');
      return false;
    }
  }, []);

  const setupOllama = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    let downloadUrl = 'https://ollama.com/download';

    if (userAgent.includes('mac')) {
      downloadUrl = 'https://ollama.com/download/mac';
    } else if (userAgent.includes('win')) {
      downloadUrl = 'https://ollama.com/download/windows';
    } else if (userAgent.includes('linux')) {
      downloadUrl = 'https://ollama.com/download/linux';
    }

    window.open(downloadUrl, '_blank');
  }, []);

  // Initialize ONCE - module-level guard prevents repeated calls
  useEffect(() => {
    initialize();
  }, [initialize]);

  const initializeWebLLM = useCallback(async () => {
    setIsWebLLMLoading(true);
    setError(null);

    try {
      await localAI.initializeWebLLM();
      setMode('backend');
      setProvider(localAI.getCurrentProvider());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize Backend AI');
    } finally {
      setIsWebLLMLoading(false);
    }
  }, []);

  return {
    mode,
    provider,
    isInitializing,
    error,
    initialize,
    generate,
    switchProvider,
    hasOllama: false,
    hasWebLLM: false,
    setupOllama,
    initializeWebLLM,
    isWebLLMLoading,
  };
};
